import React, { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";
import ItemService from "../../../../services/customer_appCommonService";
import { InputField } from "../../../../Components/formElements/InputField";
import Loader from "../../../../Components/Loader";

// =============================================================================
// ERFPaymentHistoryView
//
// Read-only. Shows payment history for the ERF and all its extensions.
//
// HOW TO USE (add this after the Remarks section in ERFDetailSummary):
//   import ERFPaymentHistoryView from "./ERFPaymentHistoryView";
//
//   {documentNumber && (
//     <ERFPaymentHistoryView documentNumber={documentNumber} />
//   )}
// =============================================================================

const ERF_TYPES_WITH_SECURITY = ["Rent", "Sale & Rent"];

// ── Shared read-only cell — disabled InputField, same pattern everywhere ──────
const ReadCell = ({ value, className = "" }) => (
  <div className="commTopButtonRightLeftPadding">
    <InputField
      className={`inputBox ${className}`}
      value={value ?? ""}
      disabled
    />
  </div>
);

const ERFPaymentHistoryView = ({ documentNumber }) => {
  const [loading, setLoading] = useState(false);
  const [erfRecord, setErfRecord] = useState(null);
  const [extensionRecords, setExtensionRecords] = useState([]);

  const [isErfVisible, setErfVisible] = useState(true);
  const [isExtVisible, setExtVisible] = useState({});

  const ConfirmStatusBadge = ({ row }) => {
    const hasSE = !!row.sellerName?.trim();
    const se = row.seConfirmation;
    const fin = row.financeConfirmation;

    if (fin?.confirmed) {
      return (
        <div>
          <span className="badge bg-success" style={{ fontSize: "10px" }}>
            ✓ Approved
          </span>
          <p className="mb-0 mt-1" style={{ fontSize: "10px", color: "#555" }}>
            Fin: {fin.confirmedBy} · {fin.confirmedAt?.slice(0, 10)}
          </p>
          {se?.confirmed && (
            <p className="mb-0" style={{ fontSize: "10px", color: "#555" }}>
              SE: {se.confirmedBy} · {se.confirmedAt?.slice(0, 10)}
            </p>
          )}
        </div>
      );
    }

    if (!hasSE) {
      return (
        <div>
          <span
            className="badge bg-info text-dark"
            style={{ fontSize: "10px" }}
          >
            Pending Finance
          </span>
          <p className="mb-0 mt-1" style={{ fontSize: "10px", color: "#888" }}>
            No SE — direct Finance
          </p>
        </div>
      );
    }

    if (se?.confirmed) {
      return (
        <div>
          <span
            className="badge bg-info text-dark"
            style={{ fontSize: "10px" }}
          >
            Pending Finance
          </span>
          <p className="mb-0 mt-1" style={{ fontSize: "10px", color: "#555" }}>
            SE: {se.confirmedBy} · {se.confirmedAt?.slice(0, 10)}
          </p>
        </div>
      );
    }

    return (
      <span className="badge bg-warning text-dark" style={{ fontSize: "10px" }}>
        Pending SE
      </span>
    );
  };

  // ── Load ERF + all extensions in parallel ─────────────────────────────────
  useEffect(() => {
    if (documentNumber) loadAllPaymentData();
    // eslint-disable-next-line
  }, [documentNumber]);

  const loadAllPaymentData = async () => {
    setLoading(true);
    try {
      const [erfResponse, extResponse] = await Promise.all([
        ItemService.generateQuery({
          queryName: "ERFDetail",
          documentNumber: documentNumber,
          timeZone: "Asia/Calcutta",
          page: 1,
          per_page: 1,
        }),
        ItemService.generateQuery({
          queryName: "ERFExtDetail",
          documentNumber: documentNumber,
          timeZone: "Asia/Calcutta",
        }),
      ]);

      if (erfResponse?.status === 200 && erfResponse.data?.length > 0) {
        setErfRecord(erfResponse.data[erfResponse.data.length - 1]);
      } else {
        toast.error("Could not load ERF payment data");
      }

      if (extResponse?.status === 200 && extResponse.data?.length > 0) {
        const sorted = [...extResponse.data].sort(
          (a, b) => new Date(a.extensionDate) - new Date(b.extensionDate),
        );
        setExtensionRecords(sorted);
        const openState = {};
        sorted.forEach((ext) => {
          openState[ext.documentNumber] = true;
        });
        setExtVisible(openState);
      }
    } catch (err) {
      console.error("ERFPaymentHistoryView load error:", err);
      toast.error("Failed to load payment history");
    } finally {
      setLoading(false);
    }
  };

  const toggleExtVisibility = (docNr) =>
    setExtVisible((prev) => ({ ...prev, [docNr]: !prev[docNr] }));

  // ── PAYMENT SUMMARY ROW ───────────────────────────────────────────────────
  const PaymentSummaryRow = ({ summary }) => {
    if (!summary) return null;
    const A = parseFloat(summary.A ?? 0).toFixed(2);
    const B = parseFloat(summary.totalCreditNote ?? summary.B ?? 0).toFixed(2);
    const C = parseFloat(summary.C ?? 0).toFixed(2);
    const balAmt = parseFloat(summary.balAmt ?? 0).toFixed(2);
    const returned = parseFloat(summary.returnedAmount ?? 0).toFixed(2);

    return (
      <div
        className="d-flex flex-wrap gap-4 px-3 py-2 mb-2"
        style={{ borderBottom: "1px solid #dee2e6", fontSize: "14px" }}
      >
        <span>
          (A) Total Received Amt till Now: <strong>{A}</strong>
        </span>
        <span>
          (B) Credit Note Amt.: <strong>{B}</strong>
        </span>
        <span>
          (C) Earlier Bal: <strong>{C}</strong>
        </span>
        <span>
          A-(B+C)) = Bal. Amt.:{" "}
          <strong style={{ color: "#198754" }}>{balAmt}</strong>
          {" / "}
          Returned: <strong style={{ color: "#198754" }}>{returned}</strong>
        </span>
      </div>
    );
  };

  // ── BILLING COLUMNS ───────────────────────────────────────────────────────
  const billingColumns = [
    {
      name: "Payment Date",
      width: "180px",
      cell: (row) => <ReadCell value={row.paymentDate} />,
    },
    {
      name: "Sale Point",
      width: "190px",
      cell: (row) => <ReadCell value={row.salePoint} />,
    },
    {
      name: "Received Location",
      width: "180px",
      cell: (row) => <ReadCell value={row.receivedLocation} />,
    },
    {
      name: "Billing Total Amt",
      width: "155px",
      cell: (row) => (
        <ReadCell value={row.billingTotalAmt} className="text-end" />
      ),
    },
    {
      name: "Payment Mode",
      width: "150px",
      cell: (row) => <ReadCell value={row.paymentMode} />,
    },
    {
      name: "Received Amt",
      width: "140px",
      cell: (row) => (
        <ReadCell value={row.billingReceivedAmt} className="text-end" />
      ),
    },
    {
      name: "Cheque No",
      width: "200px",
      cell: (row) => <ReadCell value={row.chequeNo} />,
    },
    {
      name: "Card No",
      width: "200px",
      cell: (row) => <ReadCell value={row.cardNo} />,
    },
    {
      name: "Bank",
      width: "180px",
      cell: (row) => <ReadCell value={row.bank} />,
    },
    {
      name: "Balance Amt",
      width: "130px",
      cell: (row) => (
        <ReadCell value={row.billingBalanceAmt} className="text-end" />
      ),
    },
    {
      name: "Service Executive Name",
      width: "220px",
      cell: (row) => <ReadCell value={row.sellerName} />,
    },
    {
      name: "Payment Status",
      width: "175px",
      cell: (row) => <ReadCell value={row.paymentStatus} />,
    },
    {
      name: "Confirmation",
      width: "165px",
      cell: (row) => <ConfirmStatusBadge row={row} />,
    },
  ];

  // ── SECURITY COLUMNS ──────────────────────────────────────────────────────
  const securityColumns = [
    {
      name: "Payment Date",
      width: "180px",
      cell: (row) => <ReadCell value={row.paymentDate} />,
    },
    {
      name: "Sale Point",
      width: "190px",
      cell: (row) => <ReadCell value={row.salePoint} />,
    },
    {
      name: "Received Location",
      width: "180px",
      cell: (row) => <ReadCell value={row.receivedLocation} />,
    },
    {
      name: "Total Security Amt.",
      width: "155px",
      cell: (row) => (
        <ReadCell value={row.securityTotalAmt} className="text-end" />
      ),
    },
    {
      name: "Payment Mode",
      width: "150px",
      cell: (row) => <ReadCell value={row.paymentMode} />,
    },
    {
      name: "Received Amt",
      width: "140px",
      cell: (row) => (
        <ReadCell value={row.securityReceivedAmt} className="text-end" />
      ),
    },
    {
      name: "Cheque No",
      width: "200px",
      cell: (row) => <ReadCell value={row.chequeNo} />,
    },
    {
      name: "Card No",
      width: "200px",
      cell: (row) => <ReadCell value={row.cardNo} />,
    },
    {
      name: "Bank",
      width: "180px",
      cell: (row) => <ReadCell value={row.bank} />,
    },
    {
      name: "Balance Amt",
      width: "130px",
      cell: (row) => (
        <ReadCell value={row.securityBalanceAmt} className="text-end" />
      ),
    },
    {
      name: "Service Executive Name",
      width: "190px",
      cell: (row) => <ReadCell value={row.sellerName} />,
    },
    {
      name: "Payment Status",
      width: "175px",
      cell: (row) => <ReadCell value={row.paymentStatus} />,
    },
    {
      name: "Confirmation",
      width: "165px",
      cell: (row) => <ConfirmStatusBadge row={row} />,
    },
  ];

  // ── CREDIT NOTE COLUMNS ───────────────────────────────────────────────────
  // Mirrors creditNoteColumns in ERFPaymentAdjustmentDetails — all read-only
  const creditNoteColumns = [
    {
      name: "Credit Note Date",
      width: "180px",
      cell: (row) => <ReadCell value={row.creditNoteDate} />,
    },
    {
      name: "Credit Note By",
      width: "190px",
      cell: (row) => <ReadCell value={row.creditNoteBy} />,
    },
    {
      name: "Amount Type",
      width: "180px",
      cell: (row) => <ReadCell value={row.amountType} />,
    },
    {
      name: "Status",
      width: "155px",
      cell: (row) => <ReadCell value={row.status} />,
    },
    {
      name: "Amount",
      width: "150px",
      cell: (row) => <ReadCell value={row.amount} className="text-end" />,
    },
    {
      name: "Return By",
      width: "150px",
      cell: (row) => <ReadCell value={row.returnBy} />,
    },
    {
      name: "Remarks",
      width: "200px",
      cell: (row) => <ReadCell value={row.remarks} />,
    },
    {
      name: "Refund Paid",
      width: "150px",
      cell: (row) => <ReadCell value={row.refundPaid} className="text-end" />,
    },
    {
      name: "Confirmation",
      width: "165px",
      cell: (row) => <ConfirmStatusBadge row={row} />,
    },
  ];

  // ── REUSABLE TABLES BLOCK ─────────────────────────────────────────────────
  // Used by both the ERF section and each extension section
  const PaymentTables = ({ record, showSecurity }) => (
    <>
      <PaymentSummaryRow summary={record.paymentSummary} />

      {/* Billing */}
      <h5 className="mt-3 ms-2 text-primary">Billing Amount</h5>
      <DataTable
        columns={billingColumns}
        data={record.paymentHistory ?? []}
        noDataComponent={
          <div className="p-2 text-center text-muted">
            No billing records found
          </div>
        }
      />

      {/* Security — only for Rent / Sale & Rent / Rental */}
      {showSecurity && (
        <>
          <h5 className="mt-4 ms-2 text-primary">Security Amount</h5>
          <DataTable
            columns={securityColumns}
            data={record.securityHistory ?? []}
            noDataComponent={
              <div className="p-2 text-center text-muted">
                No security records found
              </div>
            }
          />
        </>
      )}

      {/* Credit Note — always shown, matches ERFPaymentAdjustmentDetails */}
      <h5 className="mt-4 ms-2 text-primary">Credit Note Amount</h5>
      <DataTable
        columns={creditNoteColumns}
        data={record.creditNoteHistory ?? []}
        noDataComponent={
          <div className="p-2 text-center text-muted">
            No credit note records found
          </div>
        }
      />
    </>
  );

  // ── RENDER ────────────────────────────────────────────────────────────────
  if (loading) return <Loader isLoading={loading} className="text-center" />;
  if (!erfRecord && extensionRecords.length === 0) return null;

  const hasSecuritySection = ERF_TYPES_WITH_SECURITY.includes(
    erfRecord?.erfType ?? "",
  );

  return (
    <div className="accordion mb-4" id="accordionPaymentHistory">
      <div className="accordion-item">
        {/* ── Main Section Header ───────────────────────────────────────── */}
        <h2 className="accordion-header">
          <Row className="accordion-button">
            <Col xs={1} className="text-left arrowBtn">
              <i className="bi bi-credit-card-2-front"></i>
            </Col>
            <Col xs={1} className="text-center logoBtn">
              <span className="iconLogo">PH</span>
            </Col>
            <Col xs={10}>
              <span className="headerTitle">Payment History</span>
              <span className="subHeaderTitle">
                ERF and extension payment records
              </span>
            </Col>
          </Row>
        </h2>

        <div className="accordion-collapse collapse show visible">
          <div className="accordion-body">
            {/* ── 1. ERF Payment ───────────────────────────────────────── */}
            {erfRecord && (
              <div className="accordion mb-3" id="accordionErfPayment">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row className="accordion-button">
                      <Col xs={1} className="text-left arrowBtn">
                        <i
                          className={`bi ${isErfVisible ? "bi-chevron-down" : "bi-chevron-right"}`}
                          onClick={() => setErfVisible(!isErfVisible)}
                        />
                      </Col>
                      <Col xs={1} className="text-center logoBtn">
                        <span className="iconLogo">BP</span>
                      </Col>
                      <Col xs={10}>
                        <span className="headerTitle">
                          ERF Payment — {erfRecord.documentNumber}
                        </span>
                        <span className="subHeaderTitle">
                          {erfRecord.orderDate} &nbsp;|&nbsp;{" "}
                          {erfRecord.erfType} &nbsp;|&nbsp; Net Amount: ₹
                          {erfRecord.netAmount ?? 0}
                        </span>
                      </Col>
                    </Row>
                  </h2>

                  <div
                    className={`accordion-collapse collapse show ${isErfVisible ? "visible" : ""}`}
                  >
                    <div className="accordion-body">
                      <PaymentTables
                        record={erfRecord}
                        showSecurity={hasSecuritySection}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── 2. Extension Payment Sections ────────────────────────── */}
            {extensionRecords.length === 0 && (
              <div className="p-2 ms-2 text-muted">
                No extensions found for this ERF.
              </div>
            )}

            {extensionRecords.map((ext) => {
              const isOpen = isExtVisible[ext.documentNumber] ?? true;
              return (
                <div
                  key={ext.documentNumber}
                  className="accordion mb-3"
                  id={`accordionExt-${ext.documentNumber}`}
                >
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row className="accordion-button">
                        <Col xs={1} className="text-left arrowBtn">
                          <i
                            className={`bi ${isOpen ? "bi-chevron-down" : "bi-chevron-right"}`}
                            onClick={() =>
                              toggleExtVisibility(ext.documentNumber)
                            }
                          />
                        </Col>
                        <Col xs={1} className="text-center logoBtn">
                          <span className="iconLogo">EX</span>
                        </Col>
                        <Col xs={10}>
                          <span className="headerTitle">
                            Extension Payment — {ext.documentNumber}
                          </span>
                          <span className="subHeaderTitle">
                            {ext.extensionDate} &nbsp;|&nbsp;{" "}
                            {ext.extensionType} &nbsp;|&nbsp; Total Amount: ₹
                            {ext.totalAmount ?? 0}
                          </span>
                        </Col>
                      </Row>
                    </h2>

                    <div
                      className={`accordion-collapse collapse show ${isOpen ? "visible" : ""}`}
                    >
                      <div className="accordion-body">
                        {/* Extensions: security=false until confirmed with client */}
                        <PaymentTables record={ext} showSecurity={false} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ERFPaymentHistoryView;
